// @ts-check
import { Mail } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWholesaleService from '../../services/adminWholesaleService';
import { card, primaryBtn, secondaryBtn } from './shared';

let navigate = (/** @type {string} */ url) => window.location.assign(url);
/** Test hook: jsdom cannot navigate, so tests swap the redirect out. */
export const __setNavigate = (/** @type {(url: string) => void} */ fn) => {
  navigate = fn;
};

/** @param {string | null} iso */
const timeAgo = (iso) => {
  if (!iso) return 'not yet';
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return 'just now';
  const unit = (n, word) => `${n} ${word}${n === 1 ? '' : 's'} ago`;
  if (minutes < 60) return unit(minutes, 'minute');
  if (minutes < 60 * 24) return unit(Math.floor(minutes / 60), 'hour');
  return unit(Math.floor(minutes / (60 * 24)), 'day');
};

/** "Email replies": connects the mailbox that receives outreach replies so they are logged on partner timelines. */
const GmailRepliesPanel = () => {
  const toast = useToast();
  const [status, setStatus] = useState(/** @type {any} */ (null));
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const flagHandled = useRef(false);

  const load = useCallback(async () => {
    try {
      setStatus(await adminWholesaleService.gmailStatus());
    } catch (err) {
      toast.error(err.message || 'Could not load the email replies status');
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (flagHandled.current) return;
    flagHandled.current = true;
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('gmail');
    if (!flag) return;
    if (flag === 'connected') toast.success('Gmail connected. Partner replies will now be logged.');
    else toast.error('Could not connect Gmail. Try again, or check the Google setup.');
    params.delete('gmail');
    const rest = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
  }, [toast]);

  const connect = async () => {
    try {
      setBusy(true);
      const { auth_url: url } = await adminWholesaleService.gmailConnect();
      navigate(url);
    } catch (err) {
      toast.error(err.message || 'Could not start the Gmail connection');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    try {
      setBusy(true);
      setStatus(await adminWholesaleService.gmailDisconnect());
      setConfirming(false);
      toast.success('Gmail disconnected');
    } catch (err) {
      toast.error(err.message || 'Could not disconnect Gmail');
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  const reauthorise = status.last_error === 'reauthorise';

  return (
    <div className={`${card} p-4 mb-6 flex flex-wrap items-center justify-between gap-3`}>
      <div className="flex items-start gap-3 min-w-0">
        <Mail size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <h2 className="text-base font-medium">Email replies</h2>
          {!status.configured && (
            <p className="text-sm text-muted-foreground">
              {status.missing?.length
                ? `Not set up yet: missing ${status.missing.join(', ')}`
                : 'Not set up yet (needs the Google OAuth client)'}
            </p>
          )}
          {status.configured && !status.connected && (
            <p className="text-sm text-muted-foreground">
              Connect the mailbox that receives outreach replies, so a partner&apos;s reply is logged and the follow-up
              reminders stop. Read-only: it never sends or changes mail.
            </p>
          )}
          {status.connected && (
            <p className="text-sm text-muted-foreground">
              Connected as {status.account_email || 'the mailbox'} · last checked {timeAgo(status.last_checked_at)}
            </p>
          )}
          {status.connected && reauthorise && (
            <p className="text-sm text-muted-foreground">Google needs you to sign in again before replies are read.</p>
          )}
          {status.connected && status.last_error === 'backlog' && (
            <p className="text-sm text-muted-foreground">Catching up on older mail.</p>
          )}
          {status.connected && status.last_error && !reauthorise && status.last_error !== 'backlog' && (
            <p className="text-sm text-muted-foreground">Last check failed ({status.last_error}); it will retry.</p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {status.configured && !status.connected && (
          <button className={primaryBtn} disabled={busy} onClick={connect}>
            Connect Gmail
          </button>
        )}
        {status.connected && reauthorise && (
          <button className={primaryBtn} disabled={busy} onClick={connect}>
            Reconnect
          </button>
        )}
        {status.connected && !confirming && (
          <button className={secondaryBtn} disabled={busy} onClick={() => setConfirming(true)}>
            Disconnect
          </button>
        )}
        {status.connected && confirming && (
          <>
            <span className="text-sm">Stop reading replies?</span>
            <button className={primaryBtn} disabled={busy} onClick={disconnect}>
              Yes, disconnect
            </button>
            <button className={secondaryBtn} disabled={busy} onClick={() => setConfirming(false)}>
              Keep connected
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default GmailRepliesPanel;
